"use client";

import Link from "next/link";
import { useState } from "react";
import {
  CheckCircle,
  MapPin,
  Zap,
  Users,
  Eye,
  BadgeCheck,
  Sliders,
  Monitor,
  Star,
} from "lucide-react";
import Logo from "@/components/Logo";

const VOORDELEN = [
  {
    icon: Sliders,
    label: "Permanente flexibiliteit",
    items: [
      "Schaal capaciteit op of af op klantvraag",
      "Eigen monteurpool eerste prioriteit",
      "Extern professioneel netwerk als directe backup",
      "Geen vaste loonkosten of contractverplichtingen",
    ],
  },
  {
    icon: Monitor,
    label: "Korte wachttijden",
    items: [
      "Jobs geplaatst in minder dan 2 minuten",
      "Snelle matching op basis van regio en expertise",
      "Realtime inzicht in jobstatus en planning",
      "Monteurprofiel van tevoren zichtbaar voor klant",
    ],
  },
  {
    icon: Star,
    label: "Hoge klanttevredenheid",
    items: [
      "Merk-gecertificeerde monteurs per apparaat",
      "Focus op First Time Fix",
      "Garantie- én buitengarantieklussen",
      "Transparantie over wie er komt en wanneer",
    ],
  },
];

const STATS = [
  { value: "101+",    label: "Gecertificeerde monteurs" },
  { value: "10.001+", label: "Reparaties afgerond"      },
  { value: "NL",      label: "Landelijke dekking"       },
  { value: "< 2 min", label: "Job plaatsen"             },
];

const WAT_WE_BIEDEN = [
  {
    icon: Zap,
    text: "Korte wachttijden voor uw klanten — altijd snel de juiste monteur ingezet",
  },
  {
    icon: Sliders,
    text: "Permanente flexibiliteit — schaal capaciteit direct op of af zonder gedoe",
  },
  {
    icon: Star,
    text: "Hoge klanttevredenheid door gecertificeerde monteurs met focus op First Time Fix",
  },
  {
    icon: MapPin,
    text: "Landelijk dekkend netwerk van 101+ gecertificeerde witgoedmonteurs",
  },
  {
    icon: Users,
    text: "Eigen monteurpool aangevuld met extern professioneel monteurnetwerk",
  },
  {
    icon: BadgeCheck,
    text: "Merk-gecertificeerde monteurs voor garantie- én buitengarantieklussen",
  },
];

export default function AanmeldenOpdrachtgever() {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [bedrijf, setBedrijf] = useState("");
  const [email, setEmail] = useState("");
  const [telefoon, setTelefoon] = useState("");
  const [bericht, setBericht] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const subject = encodeURIComponent(`Aanvraag informatie – ${bedrijf || `${first} ${last}`}`);
    const body = encodeURIComponent(
      `Naam: ${first} ${last}\nBedrijf: ${bedrijf}\nE-mail: ${email}\nTelefoon: ${telefoon}\n\n${bericht}`
    );
    window.location.href = `mailto:info@matchrite.nl?subject=${subject}&body=${body}`;
    setTimeout(() => { setDone(true); setBusy(false); }, 500);
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface)]">

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--surface)_88%,transparent)] backdrop-blur-md w-full">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3.5">
          <Link href="/"><Logo size={18} /></Link>
          <div className="flex items-center gap-6">
            <Link href="/login" className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors hidden sm:block">
              Inloggen
            </Link>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              style={{ backgroundColor: "#E3852A" }}
            >
              Samenwerken
            </a>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-6 pb-0 pt-20 sm:pt-28">
        <p className="mb-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
          <span className="inline-block h-px w-5 bg-[var(--line)]" />
          Gemaakt voor producenten en retail
        </p>
        <h1 className="max-w-[640px] text-[clamp(34px,5.5vw,54px)] font-semibold leading-[1.06] tracking-[-0.028em] text-[var(--ink)]">
          Servicecapaciteit voor witgoed?<br />
          <span className="text-[var(--brand-strong)]">Wij leveren de monteurs.</span>
        </h1>
        <p className="mt-6 max-w-[520px] text-[16px] leading-[1.7] text-[var(--ink-2)]">
          MatchRite verbindt witgoedproducenten en retailketens met een landelijk netwerk van 101+ gecertificeerde monteurs. Schaalbaar, transparant en altijd de juiste expertise.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-medium text-[#0B0E0C] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
            style={{ backgroundColor: "#E3852A" }}
          >
            Interesse? Neem contact op
          </a>
          <a
            href="#wat-we-bieden"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-6 py-3 text-[15px] font-medium text-[var(--ink-2)] transition-all hover:-translate-y-0.5 hover:border-[var(--ink)]"
          >
            Meer ontdekken
          </a>
        </div>
      </section>

      {/* ── Stats ───────────────────────────────────────────────────── */}
      <section className="mx-auto w-full max-w-5xl px-6 pt-20 pb-0">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl bg-[var(--surface-raised)] px-6 py-5 shadow-sm">
              <p className="text-[clamp(20px,3vw,26px)] font-extrabold tracking-[-0.03em] text-[var(--ink)]">{s.value}</p>
              <p className="mt-1 text-[12px] text-[var(--ink-3)]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Wat we bieden ───────────────────────────────────────────── */}
      <section id="wat-we-bieden" className="mx-auto w-full max-w-5xl px-6 py-24">
        <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
          Wat we bieden
        </p>
        <h2 className="mb-4 text-[clamp(24px,3.5vw,32px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)]">
          Alles voor uw serviceketen, onder één dak
        </h2>
        <p className="mb-12 max-w-[520px] text-[14px] leading-[1.7] text-[var(--ink-2)]">
          Van garantieklussen tot buitengarantie, van wasmachine tot oven. MatchRite regelt de capaciteit zodat u zich kunt richten op uw klanten.
        </p>

        <div className="grid gap-4 sm:grid-cols-3">
          {WAT_WE_BIEDEN.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-start gap-4 rounded-2xl bg-[var(--surface-raised)] p-5 shadow-sm">
              <div className="mt-0.5 shrink-0 rounded-lg bg-[var(--brand-soft)] p-2">
                <Icon size={16} strokeWidth={1.75} className="text-[var(--brand-strong)]" />
              </div>
              <p className="text-[13px] leading-[1.6] text-[var(--ink-2)]">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Voordelen ───────────────────────────────────────────────── */}
      <section className="bg-[var(--surface-raised)] py-24">
        <div className="mx-auto max-w-5xl px-6">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
            MatchRite basispakket
          </p>
          <h2 className="mb-14 text-[clamp(24px,3.5vw,32px)] font-semibold leading-tight tracking-[-0.025em] text-[var(--ink)]">
            Wat u krijgt als partner
          </h2>
          <div className="grid gap-8 sm:grid-cols-3">
            {VOORDELEN.map(({ icon: Icon, label, items }) => (
              <div key={label}>
                <div className="mb-4 inline-flex items-center justify-center rounded-xl bg-[var(--brand-soft)] p-3">
                  <Icon size={20} strokeWidth={1.75} className="text-[var(--brand-strong)]" />
                </div>
                <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--brand-strong)]">
                  {label}
                </p>
                <ul className="space-y-3">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-[13px] leading-[1.6] text-[var(--ink-2)]">
                      <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: "#E3852A" }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ─────────────────────────────────────────────────── */}
      <section id="contact" className="mx-auto w-full max-w-5xl px-6 py-24">
        <div className="grid gap-16 sm:grid-cols-2 sm:items-start">

          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
              Samenwerken
            </p>
            <h2 className="mb-4 text-[clamp(26px,4vw,36px)] font-semibold leading-[1.1] tracking-[-0.025em] text-[var(--ink)]">
              Zin om samen te werken?
            </h2>
            <p className="mb-6 text-[14px] leading-[1.7] text-[var(--ink-2)]">
              Laat uw gegevens achter en we bespreken vrijblijvend de mogelijkheden voor uw organisatie. We nemen binnen één werkdag contact op.
            </p>
            <p className="text-[13px] text-[var(--ink-3)]">
              Liever direct mailen?{" "}
              <a href="mailto:info@matchrite.nl" className="text-[var(--ink)] underline underline-offset-4 hover:text-[var(--brand-strong)]">
                info@matchrite.nl
              </a>
            </p>
          </div>

          <div className="rounded-3xl bg-[var(--surface-raised)] p-8 shadow-sm">
            {done ? (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <CheckCircle size={40} strokeWidth={1.5} className="text-[var(--brand)]" />
                <h3 className="text-[20px] font-semibold tracking-[-0.015em] text-[var(--ink)]">Bedankt!</h3>
                <p className="text-[14px] leading-[1.7] text-[var(--ink-2)]">
                  We hebben uw aanvraag ontvangen en nemen binnen één werkdag contact op.
                </p>
                <Link href="/" className="mt-2 text-[13px] text-[var(--ink-3)] underline underline-offset-4 hover:text-[var(--ink)]">
                  Terug naar home
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1.5 block text-[12px] text-[var(--ink-3)]">Voornaam</span>
                    <input required value={first} onChange={(e) => setFirst(e.target.value)}
                      className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)] transition-colors" />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-[12px] text-[var(--ink-3)]">Achternaam</span>
                    <input required value={last} onChange={(e) => setLast(e.target.value)}
                      className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)] transition-colors" />
                  </label>
                </div>
                <label className="block">
                  <span className="mb-1.5 block text-[12px] text-[var(--ink-3)]">Bedrijfsnaam</span>
                  <input value={bedrijf} onChange={(e) => setBedrijf(e.target.value)}
                    className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)] transition-colors" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[12px] text-[var(--ink-3)]">E-mailadres</span>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)] transition-colors" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[12px] text-[var(--ink-3)]">Telefoonnummer <span className="opacity-60">(optioneel)</span></span>
                  <input type="tel" value={telefoon} onChange={(e) => setTelefoon(e.target.value)}
                    className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)] transition-colors" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[12px] text-[var(--ink-3)]">Waar bent u naar op zoek? <span className="opacity-60">(optioneel)</span></span>
                  <textarea rows={3} value={bericht} onChange={(e) => setBericht(e.target.value)}
                    className="w-full resize-none rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5 text-[14px] text-[var(--ink)] outline-none focus:border-[var(--ink)] transition-colors" />
                </label>
                <button type="submit" disabled={busy}
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--ink)] px-6 py-3 text-[15px] font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-50"
                >
                  {busy ? "Bezig…" : "Verstuur aanvraag"}
                </button>
                <p className="text-center text-[12px] text-[var(--ink-3)]">We nemen binnen één werkdag contact op.</p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-[var(--line)] bg-[var(--surface-raised)] px-6 py-8">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <Link href="/"><Logo size={16} /></Link>
          <p className="font-mono text-[11px] text-[var(--ink-3)]">© 2026 MatchRite · KvK 63735458</p>
        </div>
      </footer>

    </div>
  );
}
